import { useEffect, useState } from "react";

import { BriefingSchedule } from "./BriefingSchedule";
import { usePatchProfile } from "../api/hooks";
import type { Profile } from "../api/types";

const PRESETS: [string, string][] = [
  ["days", "Alle X Tage"],
  ["weekly", "Wöchentlich"],
  ["biweekly", "14-tägig"],
  ["monthly", "Monatlich"],
  ["quarterly", "Quartalsweise"],
  ["manual", "Nur manuell"],
];

const WEEKDAYS = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

export function Schaerfen({ profile }: { profile: Profile }) {
  const patch = usePatchProfile();
  const label = profile.mode === "funding" ? "Förderungen" : "Technologie";

  // --- Auftrag ---
  const [prompt, setPrompt] = useState(profile.prompt_raw);
  const [promptSaved, setPromptSaved] = useState(false);
  // Was der Lauf wirklich liest. Hat der Assistent den Auftrag geschaerft,
  // ist es dessen Fassung — der eigene Text steht dann daneben und wird
  // nicht mehr verwendet, bis man die Schaerfung verwirft.
  const refined = profile.prompt_refined.trim();

  // --- Laufplan ---
  const [preset, setPreset] = useState(profile.schedule_preset);
  const [time, setTime] = useState(profile.schedule_time);
  const [days, setDays] = useState(String(profile.schedule_every_days ?? 1));
  const validDays = /^\d+$/.test(days) && Number(days) >= 1 && Number(days) <= 365;
  const [weekday, setWeekday] = useState(profile.schedule_weekday ?? 1);
  const [monthday, setMonthday] = useState(profile.schedule_monthday ?? 1);
  const monthly = preset === "monthly" || preset === "quarterly";
  const scheduleUnchanged = preset === profile.schedule_preset && time === profile.schedule_time
    && (preset !== "days" || Number(days) === profile.schedule_every_days)
    && (preset !== "weekly" || weekday === profile.schedule_weekday)
    && (!monthly || monthday === profile.schedule_monthday);
  const [schedSaved, setSchedSaved] = useState(false);

  // --- Datenbankstruktur ---
  const [fields, setFields] = useState<string[]>(profile.card_fields);
  const [fieldInput, setFieldInput] = useState("");
  const [fieldsSaved, setFieldsSaved] = useState(false);
  const [fieldHint, setFieldHint] = useState("");

  const [refinedDraft, setRefinedDraft] = useState(profile.prompt_refined);
  const [refinedSaved, setRefinedSaved] = useState(false);
  const savedScoring = profile.scoring_prompt?.trim()
    ? profile.scoring_prompt
    : profile.scoring_prompt_default ?? "";
  const [scoring, setScoring] = useState(savedScoring);
  const [briefing, setBriefing] = useState(profile.briefing_prompt);
  const [briefingSaved, setBriefingSaved] = useState(false);

  useEffect(() => {
    setPrompt(profile.prompt_raw);
    setRefinedDraft(profile.prompt_refined);
    setBriefing(profile.briefing_prompt);
    setScoring(savedScoring);
    setPreset(profile.schedule_preset);
    setTime(profile.schedule_time);
    setDays(String(profile.schedule_every_days ?? 1));
    setWeekday(profile.schedule_weekday ?? 1);
    setMonthday(profile.schedule_monthday ?? 1);
    setFields(profile.card_fields);
  }, [profile, savedScoring]);

  const [notice, setNotice] = useState<{ message: string; kind: "success" | "error" } | null>(null);
  useEffect(() => {
    if (!notice || notice.kind === "error") return;
    const timer = window.setTimeout(() => setNotice(null), 6000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const save = (change: { id: string } & Record<string, unknown>, options?: { onSuccess?: () => void }) => {
    setNotice(null);
    patch.mutate(change, {
      onSuccess: () => {
        options?.onSuccess?.();
        const message = "prompt_raw" in change ? "Auftrag gespeichert."
          : "prompt_refined" in change ? (change.prompt_refined ? "Schärfung gespeichert." : "Schärfung verworfen.")
          : "scoring_prompt" in change ? "Gespeichert. Neubewertung startet."
          : "card_fields" in change ? "Datenbankansicht gespeichert."
          : "briefing_schedule_preset" in change ? "Briefing-Laufplan gespeichert."
          : "briefing_prompt" in change ? "Briefing-Auftrag gespeichert."
          : "notify_on_run" in change || "weekly_briefing" in change ? "E-Mail-Einstellung gespeichert."
          : "Laufplan gespeichert.";
        setNotice({ message, kind: "success" });
      },
      onError: () => setNotice({ message: "Speichern fehlgeschlagen. Bitte erneut versuchen.", kind: "error" }),
    });
  };

  const labelFor = (key: string) =>
    profile.card_field_choices.find((choice) => choice.key === key)?.label ?? key;

  const addField = () => {
    const wanted = fieldInput.trim().toLowerCase();
    if (!wanted) return;
    const choice = profile.card_field_choices.find(
      (candidate) =>
        candidate.label.toLowerCase() === wanted || candidate.key.toLowerCase() === wanted,
    );
    if (!choice) {
      setFieldHint(
        "Bitte ein Feld aus der Liste wählen.",
      );
      return;
    }
    if (!fields.includes(choice.key)) {
      setFields([...fields, choice.key]);
      setFieldsSaved(false);
    }
    setFieldInput("");
    setFieldHint("");
  };

  return (
    <>
      {notice && <div className={`save-notice ${notice.kind}`} role={notice.kind === "success" ? "status" : "alert"} aria-atomic="true">
        <span className="save-notice-icon" aria-hidden="true">{notice.kind === "success" ? "✓" : "!"}</span>
        <strong>{notice.message}</strong>
        <button type="button" aria-label="Meldung schließen" onClick={() => setNotice(null)}>×</button>
      </div>}
      <div className="view-head">
        <div>
          <h1>Suchauftrag · {label}</h1>
        </div>
      </div>

      <div className="panel" style={{ maxWidth: 760 }}>
        <div className="panel-head">
          <h2>Ihr Auftrag</h2>
        </div>
        <div className="panel-body">
          <div className="field">
            <textarea
              id="raw-input"
              rows={9}
              value={prompt}
              onChange={(event) => {
                setPrompt(event.target.value);
                setPromptSaved(false);
              }}
            />
          </div>
          <div className="action-bar">
            <span className="hint" style={{ margin: 0 }} id="prompt-status">
              {promptSaved
                ? refined
                  ? "Gespeichert. Die Schärfung hat weiterhin Vorrang."
                  : "Gespeichert."
                : ""}
            </span>
            <button
              className="btn"
              id="btn-save-prompt"
              disabled={patch.isPending || !prompt.trim()}
              onClick={() =>
                save(
                  { id: profile.id, prompt_raw: prompt },
                  { onSuccess: () => setPromptSaved(true) },
                )
              }
            >
              Speichern
            </button>
          </div>
        </div>
      </div>

      {refined && (
        <div className="panel" style={{ maxWidth: 760, marginTop: "var(--gap-md)" }}>
          <div className="panel-head">
            <h2>Vom Assistenten geschärft</h2>
          </div>
          <div className="panel-body">
            <p className="hint" style={{ margin: "0 0 12px" }}>
              Hat Vorrang vor Ihrem Auftrag.
            </p>
            <div className="field">
              <textarea
                rows={8}
                value={refinedDraft}
                onChange={(event) => {
                  setRefinedDraft(event.target.value);
                  setRefinedSaved(false);
                }}
              />
            </div>
            <div className="action-bar">
              <span className="hint" style={{ margin: 0 }}>
                {refinedSaved ? "Gespeichert." : ""}
              </span>
              <button
                className="btn secondary"
                disabled={patch.isPending}
                onClick={() =>
                  save(
                    { id: profile.id, prompt_refined: "" },
                    { onSuccess: () => setRefinedSaved(false) },
                  )
                }
              >
                Verwerfen
              </button>
              <button
                className="btn"
                disabled={patch.isPending || refinedDraft.trim() === refined}
                onClick={() =>
                  save(
                    { id: profile.id, prompt_refined: refinedDraft },
                    { onSuccess: () => setRefinedSaved(true) },
                  )
                }
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="panel" style={{ maxWidth: 760, marginTop: "var(--gap-md)" }}>
        <div className="panel-head">
          <h2>Laufplan</h2>
        </div>
        <div className="panel-body">
          <label className="check-row">
            <input
              type="checkbox"
              checked={profile.schedule_enabled}
              disabled={patch.isPending}
              onChange={(event) =>
                save({ id: profile.id, schedule_enabled: event.target.checked })
              }
            />
            <span>Suchlauf nach Zeitplan</span>
          </label>
          <label className="check-row">
            <input type="checkbox" checked={profile.notify_on_run} disabled={patch.isPending}
              onChange={(event) => save({ id: profile.id, notify_on_run: event.target.checked })} />
            <span>Änderungsberichte per E-Mail</span>
          </label>
          <div className="field-row schedule-fields">
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="field-label" htmlFor="sched-cron">
                Intervall
              </label>
              <select
                id="sched-cron"
                value={preset}
                onChange={(event) => {
                  setPreset(event.target.value as Profile["schedule_preset"]);
                  setSchedSaved(false);
                }}
              >
                {PRESETS.map(([value, text]) => (
                  <option value={value} key={value}>
                    {text}
                  </option>
                ))}
              </select>
            </div>
            {preset === "days" && <div className="field schedule-days">
              <label className="field-label" htmlFor="sched-days">Abstand in Tagen</label>
              <input type="number" id="sched-days" min={1} max={365} step={1} inputMode="numeric"
                value={days} aria-invalid={!validDays} aria-describedby={!validDays ? "sched-days-error" : undefined}
                onChange={event => { setDays(event.target.value); setSchedSaved(false); }} />
              {!validDays && <p className="hint" id="sched-days-error" role="alert">Bitte 1 bis 365 ganze Tage angeben.</p>}
            </div>}
            {preset === "weekly" && <div className="field">
              <label className="field-label" htmlFor="sched-weekday">Wochentag</label>
              <select id="sched-weekday" value={weekday} onChange={event => { setWeekday(Number(event.target.value)); setSchedSaved(false); }}>
                {WEEKDAYS.map((day, index) => <option key={day} value={index + 1}>{day}</option>)}
              </select>
            </div>}
            {monthly && <div className="field">
              <label className="field-label" htmlFor="sched-monthday">Tag des Monats</label>
              <select id="sched-monthday" value={monthday} onChange={event => { setMonthday(Number(event.target.value)); setSchedSaved(false); }}>
                {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}. des Monats</option>)}
                <option value={0}>Letzter Tag des Monats</option>
              </select>
            </div>}
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="field-label" htmlFor="sched-time">
                Startzeit
              </label>
              <input
                type="time"
                id="sched-time"
                value={time}
                disabled={preset === "manual"}
                onChange={(event) => {
                  setTime(event.target.value);
                  setSchedSaved(false);
                }}
              />
            </div>
          </div>
          {monthly && monthday > 28 && <p className="hint">In kürzeren Monaten am letzten Tag.</p>}
          <p className="hint" id="sched-preview">
            {schedSaved || scheduleUnchanged
              ? !profile.schedule_enabled ? "Zeitplan deaktiviert."
                : preset === "manual" ? "Nur manuell." : profile.schedule_preview
              : "Noch nicht gespeichert."}
          </p>
          <div className="action-bar">
            <span className="hint" style={{ margin: 0 }} id="sched-status">
              {schedSaved ? "Laufplan gespeichert." : ""}
            </span>
            <button
              className="btn"
              id="btn-save-sched"
              disabled={patch.isPending || (preset === "days" && !validDays) || (preset !== "manual" && !time)}
              onClick={() =>
                save(
                  {
                    id: profile.id, schedule_preset: preset, schedule_time: time,
                    ...(preset === "days" ? { schedule_every_days: Number(days) } : {}),
                    ...(preset === "weekly" ? { schedule_weekday: weekday } : {}),
                    ...(monthly ? { schedule_monthday: monthday } : {}),
                  },
                  { onSuccess: () => setSchedSaved(true) },
                )
              }
            >
              Speichern
            </button>
          </div>
        </div>
      </div>

      {profile.mode === "funding" && <div className="panel" style={{ maxWidth: 760, marginTop: "var(--gap-md)" }}>
        <div className="panel-head"><h2>Score-Bewertung</h2></div>
        <div className="panel-body">
          <p className="hint">Speichern startet eine Neubewertung.</p>
          <div className="field">
            <label className="field-label" htmlFor="scoring-prompt">Score-Vorgaben</label>
            <textarea id="scoring-prompt" rows={9} maxLength={6000} value={scoring}
              onChange={e => setScoring(e.target.value)} />
          </div>
          <div className="action-bar">
            <button className="btn" disabled={patch.isPending || scoring === savedScoring}
              onClick={() => save({ id: profile.id, scoring_prompt: scoring })}>Score-Vorgaben speichern</button>
          </div>
        </div>
      </div>}

      <div className="panel" style={{ maxWidth: 760, marginTop: "var(--gap-md)" }}>
        <div className="panel-head">
          <h2>Angezeigte Felder</h2>
        </div>
        <div className="panel-body">
          <div className="chip-row">
            {fields.map((key, index) => (
              <span className="chip" key={key}>
                {labelFor(key)}
                <button
                  className="chip-x"
                  aria-label="Entfernen"
                  onClick={() => {
                    setFields(fields.filter((_, i) => i !== index));
                    setFieldsSaved(false);
                  }}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              className="chip-input"
              id="field-input"
              placeholder="Feld hinzufügen ⏎"
              list="field-catalog"
              value={fieldInput}
              onChange={(event) => setFieldInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") addField();
              }}
            />
            <datalist id="field-catalog">
              {profile.card_field_choices
                .filter((choice) => !fields.includes(choice.key))
                .map((choice) => (
                  <option value={choice.label} key={choice.key} />
                ))}
            </datalist>
          </div>
          {fieldHint && (
            <p className="hint" style={{ marginTop: 8 }}>
              {fieldHint}
            </p>
          )}
          <div className="action-bar">
            <span className="hint" style={{ margin: 0 }} id="fields-status">
              {fieldsSaved ? "Gespeichert." : ""}
            </span>
            <button
              className="btn"
              id="btn-save-fields"
              disabled={patch.isPending}
              onClick={() =>
                save(
                  { id: profile.id, card_fields: fields },
                  { onSuccess: () => setFieldsSaved(true) },
                )
              }
            >
              Speichern
            </button>
          </div>
        </div>
      </div>

      <div className="panel" style={{ maxWidth: 760, marginTop: "var(--gap-md)" }}>
        <div className="panel-head">
          <h2>Wochenbriefing</h2>
        </div>
        <div className="panel-body">
          <p className="hint" style={{ margin: "0 0 12px" }}>
            Versand unabhängig vom Suchlauf.
          </p>
          <BriefingSchedule profile={profile} save={save} pending={patch.isPending} />
          <div className="field">
            <label className="field-label" htmlFor="briefing-input">Briefing-Auftrag</label>
            <textarea
              id="briefing-input"
              rows={5}
              value={briefing}
              placeholder="Schwerpunkte (optional)"
              onChange={(event) => {
                setBriefing(event.target.value);
                setBriefingSaved(false);
              }}
            />
          </div>
          <div className="action-bar">
            <span className="hint" style={{ margin: 0 }} id="briefing-status">
              {briefingSaved ? "Gespeichert." : ""}
            </span>
            <button
              className="btn"
              id="btn-save-briefing"
              disabled={patch.isPending || briefing === profile.briefing_prompt}
              onClick={() =>
                save(
                  { id: profile.id, briefing_prompt: briefing },
                  { onSuccess: () => setBriefingSaved(true) },
                )
              }
            >
              Speichern
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
